import { Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { differenceInMinutes } from 'date-fns'
import { useFormContext, useWatch } from 'react-hook-form'
import { theme } from '../../../../../../../modules/App/Providers/Theme/theme'
import { ReservationFormData } from '../../../constants'
import { Check as CheckIcon } from '@mui/icons-material'
import { Button } from '@mui/material'
import { addMinutes, isEqual, setHours, setMinutes, startOfDay } from 'date-fns'
import { useCallback } from 'react'
import { useFormat, useLocale } from '../../../../../../../hooks'
import { ResourceAvailability } from '../../../../../../../types/mergedAvailabilities'
import { ResourcePair } from '../../../../../../../types'
import { logFactory } from '../../../../../../../util'

const log = logFactory('ReservationItem')

type Props = {
  slot: ResourceAvailability
  eligibleResourcePairs?: ResourcePair[]
  showDuration?: boolean
}

const standardDateFormatOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', hourCycle: 'h12', minute: '2-digit' }

const StyledButton = styled(Button)`
  &[data-selected='true'] {
    background-color: ${({ theme }) => theme.palette.primary.main};
    color: ${({ theme }) => theme.palette.common.white};

    & * [x-duration] {
      color: ${theme.palette.grey[200]};
    }
  }
`

const Check = styled(CheckIcon)`
  justify-self: start;
  margin-right: 0.5rem;
  left: 0rem;
  opacity: 0;
  transform: scale(0%);
  transition: 200ms transform ease-in;

  &[data-show='true'] {
    left: 0.25rem;
    opacity: 1;
    transform: scale(100%);
  }
`

const Container = styled('div')`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  grid-auto-flow: column;
  width: 100%;
`

const Item = ({ slot, eligibleResourcePairs }: Props) => {
  const { hourCycle } = useLocale()
  const { date_end, date_start, resource_ids } = slot
  const blockDurationInMinutes = differenceInMinutes(date_end, date_start)

  const [timezone, durationInMinutes, dateStartSelected] = useWatch<ReservationFormData>({
    name: ['timezone', 'durationInMinutes', 'dateStartSelected'],
  })

  const { setValue } = useFormContext<ReservationFormData>()

  const { formatDate: formatCustomerDate } = useFormat({
    dateOptions: { ...standardDateFormatOptions, hourCycle, timeZone: timezone as string },
  })

  const customerDisplayDate = formatCustomerDate(date_start)
  const isSelected =
    isEqual(date_start, dateStartSelected as Date) && isEqual(date_end, addMinutes(dateStartSelected as Date, durationInMinutes as number))

  const onClick = useCallback(() => {
    // Check if ANY eligible resource pair has a cabin (cabinId present)
    // This handles the case where multiple slots at the same time exist
    // and some have cabins while others don't
    const hasCombinedPairs = eligibleResourcePairs?.some(([, cabinId]) => cabinId !== undefined) ?? false
    const hasCabin = resource_ids.length > 1 || hasCombinedPairs

    log('🖱️ Slot clicked:', {
      date_start: date_start.toISOString(),
      date_end: date_end.toISOString(),
      resource_ids,
      eligibleResourcePairs,
      hasCabin,
      hasCombinedPairs,
    })

    if (isSelected) {
      setValue('dateStartSelected', undefined)
      setValue('isCombinedBooking', false)
      setValue('eligibleResourcePairs', undefined)
      return
    }

    const startTimeWithHourMinutes = setMinutes(setHours(startOfDay(date_start), date_start.getHours()), date_start.getMinutes())

    setValue('dateStartSelected', startTimeWithHourMinutes)
    setValue('durationInMinutes', blockDurationInMinutes)
    setValue('resourceIds', resource_ids)
    setValue('isCombinedBooking', hasCabin)

    if (hasCabin && eligibleResourcePairs?.length) {
      // Store all eligible pairs for random selection at submission
      // Filter to only include combined pairs (with cabin) if any exist
      const combinedPairs = eligibleResourcePairs.filter(([, cabinId]) => cabinId !== undefined)
      setValue('eligibleResourcePairs', combinedPairs.length > 0 ? combinedPairs : eligibleResourcePairs)
    } else {
      setValue('eligibleResourcePairs', undefined)
    }
  }, [date_start, date_end, blockDurationInMinutes, isSelected, resource_ids, eligibleResourcePairs, setValue])

  return (
    <li>
      <StyledButton data-date={date_start.toISOString()} data-selected={isSelected} onClick={onClick} fullWidth variant="outlined">
        <Container>
          {isSelected ? <Check data-show /> : <span />}
          <Typography>{customerDisplayDate}</Typography>
        </Container>
      </StyledButton>
    </li>
  )
}

export { Item }
